import React, {
  useCallback,
  useState,
  useEffect,
  useRef,
  useMemo,
} from "react";
import KlydoLabels from "./KlydoLabels";
import FirmwareKlydos from "./FirmwareKlydos";
import PageHeader from "components/PageHeader";
import FilterBar from "components/FilterBar";
import DSSegmentedTabs from "views/system-design/DSSegmentedTabs";
import { useSearchParams } from "react-router-dom";
import KlydoModal from "./KlydoModal";
import SendKlydoModal from "./SendKlydoModal";
import { FaCheckCircle, FaHeart, FaStar } from "react-icons/fa";
import { useKlydoRatingStatsBatch } from "../hooks/useKlydoRatingStats";
import DSCheckbox from "../../system-design/DSCheckBox";
import MsTable from "../../../components/Widgets/Table";
import { BsTag } from "react-icons/bs";
// eslint-disable-next-line import/no-unresolved
import { debounce } from "lodash";
import { FaCircleXmark } from "react-icons/fa6";
import { Autocomplete, useTheme } from "@mui/material";
import DSTextField from "../../system-design/DSTextField";
import DSMenuItem from "../../system-design/DSMenuItem";
import DSPrimaryButton from "../../system-design/DSPrimaryButton";
import DSSecondaryButton from "../../system-design/DSSecondaryButton";
import DSCircularProgress from "../../system-design/DSCircularProgress";
import useAllKlydoLabels from "../hooks/useAllKlydoLabels";
import useSealedKlydos, {
  DisplayType,
  SearchType,
} from "../../../components/hooks/useSealedKlydos";
import id from "../../../friendly-id/id-gen";
import DSStack from "../../system-design/DSStack";
import DSTypography from "../../system-design/DSTypography";
import { Klydo, KlydoSearchFilter, KlydoType } from "../../../api/klydo";
import DSBox from "../../system-design/DSBox";
import DSGrid from "../../system-design/DSGrid";
import DSCard from "../../system-design/DSCard";
import DSChip from "../../system-design/DSChip";
import DSDialog from "../../system-design/DSDialog";
import useTabParam from "components/hooks/useTabParam";
import { TABLET } from "consts/breakpoints";
import { useOnScreen } from "../../../components/hooks/useOnScreen";

type TriState = "ALL" | "TRUE" | "FALSE";

const PAGE_SIZE = 20;

const GifThumbnail = ({ loopUrl }: { loopUrl: string }) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const isOnScreen = useOnScreen(containerRef);

  if (!loopUrl) {
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        <p>File type not supported</p>
      </div>
    );
  }

  return (
    <div
      ref={containerRef}
      style={{
        borderRadius: "50%",
        width: 96,
        height: 96,
        position: "relative",
        overflow: "hidden",
      }}
    >
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          position: "absolute",
          top: 0,
          left: 0,
          width: "100%",
          aspectRatio: "1/1",
        }}
      >
        {isOnScreen && (
          <video
            muted
            src={loopUrl}
            loop
            autoPlay
            style={{ width: "auto", height: "100%", objectFit: "cover" }}
          />
        )}
      </div>
    </div>
  );
};

function KlydosList() {
  const { spacing } = useTheme();
  const [searchParams, setSearchParams] = useSearchParams();
  const [data, setData] = useState<Array<Klydo>>();

  const [showSendKlydoModal, setShowSendKlydoModal] = useState(false);

  const [localSelected, setLocalSelected] = useState<Array<Klydo>>([]);
  const [greetFilter, setGreetFilter] = useState<TriState>("ALL");
  const [publicFilter, setPublicFilter] = useState<TriState>("ALL");
  const [collectionFilter, setCollectionFilter] = useState<TriState>("ALL");
  const [chimeFilter, setChimeFilter] = useState<TriState>("ALL");
  const [aiGeneratedFilter, setAiGeneratedFilter] = useState<TriState>("ALL");
  const [tagFilter, setTagFilter] = useState<string[]>([]);
  const [tagMatchMode, setTagMatchMode] = useState<"all" | "any">("all");
  const [ratingStageOnly, setRatingStageOnly] = useState(false);
  // Which discriminator the gallery queries. "SEALED" is the historical
  // behaviour (published klydos); "DRAFT" and "ALL" go through the base-model
  // admin text search.
  const [klydoTypeFilter, setKlydoTypeFilter] = useState<
    "SEALED" | "DRAFT" | "ALL"
  >("SEALED");
  const { data: allLabels = [], isLoading: labelsLoading } =
    useAllKlydoLabels();
  const tagOptions = useMemo(() => allLabels.map((l) => l.text), [allLabels]);
  const [sortField, setSortField] = useState<string>("createdAt");
  const [sortDirection, setSortDirection] = useState<number>(-1);
  // grid is the better default on small screens
  const [displayType, setDisplayType] = useState<DisplayType>(() =>
    window.matchMedia(`(max-width: ${TABLET}px)`).matches ? "GRID" : "LIST",
  );
  const [shouldShowKlydoModal, setShouldShowKlydoModal] =
    useState<boolean>(false);
  const [selectedKlydo, setSelectedKlydo] = useState<Klydo | undefined>();

  const [localSearch, setLocalSearch] = useState(
    searchParams.get("search") || "",
  );

  useEffect(() => {
    setLocalSearch(searchParams.get("search") || "");
  }, [searchParams]);

  const debouncedSetSearchParams = useCallback(
    // functional form preserves other params (e.g. ?tab=) instead of
    // replacing the whole query string
    debounce(
      (s: string) =>
        setSearchParams(
          (prev) => {
            const params = new URLSearchParams(prev);
            if (s) params.set("search", s);
            else params.delete("search");
            return params;
          },
          { replace: true },
        ),
      500,
    ),
    [],
  );

  useEffect(
    () => () => debouncedSetSearchParams.cancel(),
    [debouncedSetSearchParams],
  );

  const handleFreeSearch = (value: string) => {
    setLocalSearch(value);
    debouncedSetSearchParams(value);
  };

  const [searchType, setSearchType] = useState<SearchType>("TEXT");
  const [useGeminiEmbedding, setUseGeminiEmbedding] = useState(false);
  const [imageBase64, setImageBase64] = useState<string | undefined>();
  const [imagePreviewUrl, setImagePreviewUrl] = useState<string | undefined>();
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const handleImageFile = useCallback((file: File) => {
    if (!file.type.startsWith("image/")) return;
    const reader = new FileReader();
    reader.onload = () => {
      const dataUrl = reader.result as string;
      setImagePreviewUrl(dataUrl);
      const commaIdx = dataUrl.indexOf(",");
      setImageBase64(commaIdx >= 0 ? dataUrl.slice(commaIdx + 1) : dataUrl);
    };
    reader.readAsDataURL(file);
  }, []);

  const clearImage = useCallback(() => {
    setImageBase64(undefined);
    setImagePreviewUrl(undefined);
    if (fileInputRef.current) fileInputRef.current.value = "";
  }, []);

  useEffect(() => {
    if (searchType !== "SIMILARITY") return;
    const onPaste = (event: Event) => {
      const e = event as ClipboardEvent;
      const items = e.clipboardData?.items;
      if (!items) return;
      for (const item of Array.from(items)) {
        if (item.kind === "file" && item.type.startsWith("image/")) {
          const file = item.getAsFile();
          if (file) {
            e.preventDefault();
            handleImageFile(file);
            return;
          }
        }
      }
    };
    document.addEventListener("paste", onPaste);
    return () => document.removeEventListener("paste", onPaste);
  }, [searchType, handleImageFile]);

  useEffect(() => {
    if (searchType === "TEXT" && imageBase64) {
      clearImage();
    }
  }, [searchType, imageBase64, clearImage]);

  const buildFilters = (): KlydoSearchFilter => {
    const filters: KlydoSearchFilter = {};
    if (greetFilter !== "ALL") filters.greet = greetFilter === "TRUE";
    if (publicFilter !== "ALL") filters.isPublic = publicFilter === "TRUE";
    if (collectionFilter !== "ALL")
      filters.inCollection = collectionFilter === "TRUE";
    if (chimeFilter !== "ALL") filters.hasCustomChime = chimeFilter === "TRUE";
    if (aiGeneratedFilter !== "ALL")
      filters.generatedByAI = aiGeneratedFilter === "TRUE";
    if (tagFilter.length) {
      filters.tags = tagFilter;
      if (tagMatchMode !== "all") filters.tagsMatchMode = tagMatchMode;
    }
    if (ratingStageOnly) filters.ratingStage = true;
    return filters;
  };

  const filters = buildFilters();
  const activeFilterCount = Object.keys(filters).length;

  const klydoType =
    klydoTypeFilter === "ALL"
      ? undefined
      : klydoTypeFilter === "DRAFT"
        ? KlydoType.Draft
        : KlydoType.Sealed;

  const {
    data: klydos,
    fetchNextPage,
    isLoading,
    isFetchingNextPage,
    hasNextPage,
    totalCount,
  } = useSealedKlydos(
    searchParams.get("search") || "",
    PAGE_SIZE,
    searchType,
    filters,
    false,
    sortField,
    sortDirection,
    imageBase64,
    useGeminiEmbedding,
    klydoType,
  );

  const { data: ratingStatsById } = useKlydoRatingStatsBatch(
    (klydos ?? []).map((k) => k.id),
  );

  const loadMoreRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (displayType !== "GRID") return;
    const sentinel = loadMoreRef.current;
    if (!sentinel) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasNextPage && !isFetchingNextPage) {
          fetchNextPage();
        }
      },
      { rootMargin: "200px" },
    );
    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [displayType, hasNextPage, isFetchingNextPage, fetchNextPage, klydos]);

  const handleSortClick = useCallback(
    (field: string) => {
      if (field === "favorites") {
        if (sortField === "stats.favorites") {
          setSortDirection(-sortDirection);
        } else {
          setSortField("stats.favorites");
        }
      } else {
        if (field === sortField) {
          setSortDirection(-sortDirection);
        }
        setSortField(field);
      }
    },
    [sortDirection, sortField],
  );

  const tableColumns: Array<{
    sort: boolean;
    field: string;
    label: string;
    size?: number;
    noHeadline?: boolean;
  }> = [
    {
      sort: false,
      field: "tag",
      label: "",
      size: 32,
    },
    {
      sort: false,
      field: "index",
      label: "#",
      size: 60,
    },
    {
      sort: true,
      label: "id",
      field: "id",
      size: 200,
    },
    {
      sort: true,
      label: "author",
      field: "authorName",
      size: 200,
    },
    {
      sort: true,
      label: "name",
      field: "name",
      size: 200,
    },
    {
      sort: false,
      label: "gif",
      field: "image",
    },
    {
      sort: true,
      label: "date",
      field: "createdAt",
      size: 220,
    },
    {
      size: 150,
      sort: true,
      label: "Favorite",
      field: "favorites",
    },
    {
      size: 120,
      sort: true,
      label: "Avg Rating",
      field: "avgRating",
    },
    {
      size: 110,
      sort: true,
      label: "Rating Count",
      field: "ratingCount",
    },
    {
      size: 128,
      sort: false,
      label: "Unlisted",
      field: "unlisted",
    },
    {
      size: 128,
      sort: false,
      label: "Public",
      field: "public",
    },
    {
      size: 128,
      sort: false,
      label: "task",
      field: "task",
    },
  ];
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const tableRows = (r: Klydo, key: number) => {
    return {
      index: <p style={{ paddingRight: "8px" }}>{key + 1}</p>,
      id: <p>{r.id}</p>,
      image: <GifThumbnail loopUrl={r.klydoAnimation?.xs ?? r.loopUrl} />,
      authorName: <p title={r.creator}>{r.author?.name}</p>,
      name: r.name,
      createdAt: r.createdAt?.toLocaleString("en-GB"),
      favorites: (
        <div
          style={{
            display: "flex",
            justifyContent: "center",
          }}
        >
          {r?.stats?.favorites}
        </div>
      ),

      avgRating: (
        <div style={{ display: "flex", justifyContent: "center" }}>
          {ratingStatsById?.[r.id]?.count
            ? ratingStatsById[r.id].average.toFixed(1)
            : "-"}
        </div>
      ),
      ratingCount: (
        <div style={{ display: "flex", justifyContent: "center" }}>
          {ratingStatsById?.[r.id]?.count ?? 0}
        </div>
      ),
      public: <p>{(!!r.public?.status).toString()}</p>,
      unlisted: <p>{(!!r.unlisted).toString()}</p>,
      task: (
        <div
          style={{
            display: "flex",
            justifyContent: "center",
          }}
        >
          {r.inTasks ? (
            <FaCheckCircle color="green" />
          ) : (
            <FaCircleXmark color="red" />
          )}
        </div>
      ),
      key: r.id,
      tag: r.tags?.length ? (
        <BsTag title={"tags:" + r.tags?.reduce((a, b) => a + "\n" + b, "")} />
      ) : (
        ""
      ),
    };
  };

  function genActions() {
    return (
      <DSBox
        sx={{
          display: "flex",
          gap: 1,
        }}
      >
        <DSPrimaryButton
          size="small"
          sx={{ mb: "5px" }}
          onClick={() => setShowSendKlydoModal(true)}
        >
          {localSelected.length === 1 ? "Send Klydo" : "Send Klydos"}
        </DSPrimaryButton>
      </DSBox>
    );
  }

  const handleKlydoUpdate = (klydo: Klydo) => {
    if (klydo) {
      setData((data ?? []).map((k: Klydo) => (k.id === klydo?.id ? klydo : k)));
    }
  };

  // plain JSX (not a nested component) so its element identity is stable
  // across renders — a nested component type remounts the subtree each render
  const klydosFilter = (
    <DSBox display="flex" justifyContent="flex-start" flexWrap="wrap" gap={2}>
      <DSBox display="flex" alignItems="center" gap={1}>
        <DSTypography variant="caption">Greet:</DSTypography>
        <DSTextField
          select
          size="small"
          sx={{ width: "100px" }}
          value={greetFilter}
          onChange={(e) => setGreetFilter(e.target.value as TriState)}
        >
          <DSMenuItem value="ALL">All</DSMenuItem>
          <DSMenuItem value="TRUE">Yes</DSMenuItem>
          <DSMenuItem value="FALSE">No</DSMenuItem>
        </DSTextField>
      </DSBox>
      <DSBox display="flex" alignItems="center" gap={1}>
        <DSTypography variant="caption">Public:</DSTypography>
        <DSTextField
          select
          size="small"
          sx={{ width: "100px" }}
          value={publicFilter}
          onChange={(e) => setPublicFilter(e.target.value as TriState)}
        >
          <DSMenuItem value="ALL">All</DSMenuItem>
          <DSMenuItem value="TRUE">Yes</DSMenuItem>
          <DSMenuItem value="FALSE">No</DSMenuItem>
        </DSTextField>
      </DSBox>
      <DSBox display="flex" alignItems="center" gap={1}>
        <DSTypography variant="caption">In Collection:</DSTypography>
        <DSTextField
          select
          size="small"
          sx={{ width: "120px" }}
          value={collectionFilter}
          onChange={(e) => setCollectionFilter(e.target.value as TriState)}
        >
          <DSMenuItem value="ALL">All</DSMenuItem>
          <DSMenuItem value="TRUE">Yes</DSMenuItem>
          <DSMenuItem value="FALSE">No</DSMenuItem>
        </DSTextField>
      </DSBox>
      <DSBox display="flex" alignItems="center" gap={1}>
        <DSTypography variant="caption">Custom Chime:</DSTypography>
        <DSTextField
          select
          size="small"
          sx={{ width: "120px" }}
          value={chimeFilter}
          onChange={(e) => setChimeFilter(e.target.value as TriState)}
        >
          <DSMenuItem value="ALL">All</DSMenuItem>
          <DSMenuItem value="TRUE">Yes</DSMenuItem>
          <DSMenuItem value="FALSE">No</DSMenuItem>
        </DSTextField>
      </DSBox>
      <DSBox display="flex" alignItems="center" gap={1}>
        <DSTypography variant="caption">AI Generated:</DSTypography>
        <DSTextField
          select
          size="small"
          sx={{ width: "120px" }}
          value={aiGeneratedFilter}
          onChange={(e) => setAiGeneratedFilter(e.target.value as TriState)}
        >
          <DSMenuItem value="ALL">All</DSMenuItem>
          <DSMenuItem value="TRUE">Yes</DSMenuItem>
          <DSMenuItem value="FALSE">No</DSMenuItem>
        </DSTextField>
      </DSBox>
      <DSBox display="flex" alignItems="center" gap={1}>
        <DSTypography variant="caption">Tags:</DSTypography>
        <Autocomplete
          multiple
          freeSolo
          size="small"
          options={tagOptions}
          value={tagFilter}
          onChange={(_, v) => setTagFilter(v as string[])}
          loading={labelsLoading}
          renderTags={(value, getTagProps) =>
            value.map((option, index) => (
              <DSChip
                label={option}
                size="small"
                {...getTagProps({ index })}
                key={option}
              />
            ))
          }
          renderInput={(params) => (
            <DSTextField
              {...params}
              placeholder={tagFilter.length ? "" : "Add tag…"}
            />
          )}
          sx={{ minWidth: 260 }}
        />
        <DSTextField
          select
          size="small"
          sx={{ width: "90px" }}
          value={tagMatchMode}
          onChange={(e) => setTagMatchMode(e.target.value as "all" | "any")}
          disabled={tagFilter.length < 2}
          title="How multiple tags combine"
        >
          <DSMenuItem value="all">AND</DSMenuItem>
          <DSMenuItem value="any">OR</DSMenuItem>
        </DSTextField>
      </DSBox>
    </DSBox>
  );

  return (
    <>
      <FilterBar
        search={{
          value: localSearch,
          onChange: handleFreeSearch,
          placeholder: "Search klydos…",
        }}
        activeCount={activeFilterCount}
        quickActions={
          <>
            <DSSegmentedTabs<SearchType>
              size="small"
              options={[
                { label: "Text", value: "TEXT" },
                { label: "Semantic", value: "SIMILARITY" },
              ]}
              value={searchType}
              onChange={(v) => {
                if (v) setSearchType(v);
              }}
            />
            {searchType === "SIMILARITY" && (
              <DSStack
                direction="row"
                spacing={1}
                sx={{ alignItems: "center" }}
              >
                <DSStack
                  direction="row"
                  sx={{ alignItems: "center" }}
                  title="Search with the Gemini Embedding 2 model instead of the current embedding model"
                >
                  <DSCheckbox
                    size="small"
                    checked={useGeminiEmbedding}
                    onChange={(e) => setUseGeminiEmbedding(e.target.checked)}
                  />
                  <DSTypography variant="caption">
                    Embedding v2 (Gemini)
                  </DSTypography>
                </DSStack>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  style={{ display: "none" }}
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    if (file) handleImageFile(file);
                  }}
                />
                {imagePreviewUrl ? (
                  <DSBox
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                      border: "1px solid",
                      borderColor: "divider",
                      borderRadius: 1,
                      padding: "2px 6px 2px 2px",
                    }}
                  >
                    <img
                      src={imagePreviewUrl}
                      alt="search"
                      style={{
                        width: 32,
                        height: 32,
                        objectFit: "cover",
                        borderRadius: 4,
                      }}
                      title="Searching by this image"
                    />
                    <DSTypography variant="caption">Image search</DSTypography>
                    <FaCircleXmark
                      style={{ cursor: "pointer" }}
                      onClick={clearImage}
                      title="Clear image"
                    />
                  </DSBox>
                ) : (
                  <DSSecondaryButton
                    size="small"
                    onClick={() => fileInputRef.current?.click()}
                    title="Upload an image, or paste one with Cmd/Ctrl+V"
                  >
                    Search by image
                  </DSSecondaryButton>
                )}
              </DSStack>
            )}
            <DSSegmentedTabs<DisplayType>
              size="small"
              options={[
                { label: "List", value: "LIST" },
                { label: "Grid", value: "GRID" },
              ]}
              value={displayType}
              onChange={(v) => {
                if (v) setDisplayType(v);
              }}
            />
            <DSBox sx={{ display: "flex", alignItems: "center", ml: 1 }}>
              <DSCheckbox
                checked={ratingStageOnly}
                onChange={(e) => setRatingStageOnly(e.target.checked)}
              />
              <DSTypography variant="caption">Rating stage only</DSTypography>
            </DSBox>
            <DSTextField
              select
              size="small"
              sx={{ width: "120px", ml: 1 }}
              value={klydoTypeFilter}
              onChange={(e) =>
                setKlydoTypeFilter(e.target.value as "SEALED" | "DRAFT" | "ALL")
              }
            >
              <DSMenuItem value="SEALED">Sealed</DSMenuItem>
              <DSMenuItem value="DRAFT">Draft</DSMenuItem>
              <DSMenuItem value="ALL">All</DSMenuItem>
            </DSTextField>
            {totalCount !== undefined && (
              <DSTypography
                variant="body2"
                sx={{ ml: "auto", color: "text.secondary" }}
              >
                {totalCount.toLocaleString()} klydos
              </DSTypography>
            )}
          </>
        }
      >
        {klydosFilter}
      </FilterBar>
      {localSelected.length > 0 && (
        <DSBox sx={{ mb: "10px" }}>
          {genActions()}
          <DSSecondaryButton
            size="small"
            sx={{ mb: "5px" }}
            onClick={() => {
              setLocalSelected([]);
            }}
          >
            Exit
          </DSSecondaryButton>
        </DSBox>
      )}
      {klydos && !isLoading ? (
        displayType === "LIST" ? (
          <MsTable
            id="klydos"
            cancelSort={true}
            actions={() => <></>}
            onScrollDown={fetchNextPage}
            onSelectedChanged={(selected: Array<Klydo>) => {
              setLocalSelected(selected);
            }}
            onAction={(selected: Array<Klydo>) => {
              setLocalSelected(selected);
            }}
            rowBuild={tableRows}
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            search={(row: any, val: string) => {
              return (
                id.friendlyFromHex(row.id).includes(val) ||
                id.friendlyFromHex(row.id).includes(val) ||
                row.author?.toLowerCase().includes(val) ||
                row.name.toLowerCase().includes(val)
              );
            }}
            data={klydos}
            initSort={"createdAt"}
            cols={tableColumns}
            title=""
            handleChangedKlydo={handleKlydoUpdate}
            modal={KlydoModal}
            shouldHideSearchBar
            onSortClick={handleSortClick}
          />
        ) : (
          <DSBox sx={{ overflowY: "scroll", height: "100%" }}>
            <DSGrid
              container
              sx={{
                display: "grid",
                gridTemplateColumns: {
                  xxs: "repeat(2, 1fr)",
                  md: "repeat(3, 1fr)",
                  xl: "repeat(5, 1fr)",
                },
              }}
            >
              {(klydos ?? []).map((klydo) => (
                <DSGrid item key={klydo.id} sx={{ padding: spacing(1) }}>
                  <DSCard
                    sx={{
                      width: "95%",
                      height: "240px",
                      alignItems: "center",
                      borderRadius: "12px",
                      flexDirection: "column",
                      justifyContent: "center",
                      display: "flex",
                      position: "relative",
                    }}
                    onClick={() => {
                      setShouldShowKlydoModal(true);
                      setSelectedKlydo(klydo);
                    }}
                  >
                    <DSBox
                      sx={{
                        position: "absolute",
                        top: spacing(1),
                        left: spacing(1),
                        zIndex: 1,
                      }}
                      onClick={(e) => e.stopPropagation()}
                    >
                      <DSCheckbox
                        checked={localSelected.some((f) => f.id === klydo.id)}
                        onChange={(e) => {
                          e.stopPropagation();
                          const newSelected = e.target.checked
                            ? [...localSelected, klydo]
                            : localSelected.filter((s) => s.id !== klydo.id);
                          setLocalSelected(newSelected);
                        }}
                      />
                    </DSBox>
                    <DSBox
                      sx={{
                        position: "absolute",
                        top: spacing(1),
                        right: spacing(1),
                        zIndex: 1,
                        display: "flex",
                        alignItems: "center",
                        gap: 0.5,
                        color: "text.secondary",
                      }}
                      title="Favorites"
                    >
                      <FaHeart color="#e0245e" />
                      <DSTypography variant="caption">
                        {klydo.stats?.favorites ?? 0}
                      </DSTypography>
                    </DSBox>
                    <DSBox
                      sx={{
                        position: "absolute",
                        top: spacing(4),
                        right: spacing(1),
                        zIndex: 1,
                        display: "flex",
                        alignItems: "center",
                        gap: 0.5,
                        color: "text.secondary",
                      }}
                      title="Average rating (votes)"
                    >
                      <FaStar color="#FFC107" />
                      <DSTypography variant="caption">
                        {ratingStatsById?.[klydo.id]?.count
                          ? `${ratingStatsById[klydo.id].average.toFixed(1)} (${
                              ratingStatsById[klydo.id].count
                            })`
                          : "-"}
                      </DSTypography>
                    </DSBox>
                    <GifThumbnail
                      loopUrl={klydo.klydoAnimation?.xs ?? klydo.loopUrl}
                    />

                    <DSTypography>{klydo.name}</DSTypography>
                    <DSTypography>{klydo.author?.name}</DSTypography>
                    <DSBox
                      sx={{
                        display: "flex",
                        flexDirection: "row",
                        justifyContent: "center",
                        gap: spacing(1),
                      }}
                    >
                      <DSChip
                        variant={klydo.public?.status ? "filled" : "outlined"}
                        label="Public"
                      />
                      <DSChip
                        variant={klydo.unlisted ? "filled" : "outlined"}
                        label="Unlisted"
                      />
                      <DSChip
                        variant={klydo.inTasks ? "filled" : "outlined"}
                        label="Task"
                      />
                    </DSBox>
                  </DSCard>
                </DSGrid>
              ))}
            </DSGrid>
            <div ref={loadMoreRef} style={{ height: 1 }} />
          </DSBox>
        )
      ) : (
        <DSCircularProgress />
      )}

      {selectedKlydo && (
        <DSDialog
          fullWidth
          maxWidth="lg"
          open={shouldShowKlydoModal}
          onClose={() => setShouldShowKlydoModal(false)}
        >
          <KlydoModal
            klydoId={selectedKlydo.id}
            setShowModal={setShouldShowKlydoModal}
          />
        </DSDialog>
      )}
      {localSelected.length > 0 && (
        <SendKlydoModal
          open={showSendKlydoModal}
          handleClose={() => setShowSendKlydoModal(false)}
          klydos={localSelected}
        />
      )}
    </>
  );
}

const Klydos = () => {
  const [tabParam, setLayout] = useTabParam("tab", "klydos", [
    "klydos",
    "labels",
    "firmware-klydos",
  ]);
  const layout = tabParam as
    | "klydos"
    | "labels"
    | "firmware-klydos"
    | undefined;

  return (
    <>
      <PageHeader
        overline="Content"
        title="Klydos"
        subtitle="Animations and clock faces across the platform"
        tabs={
          <DSSegmentedTabs<"klydos" | "labels" | "firmware-klydos">
            options={[
              { label: "Klydos", value: "klydos" },
              { label: "Tags", value: "labels" },
              { label: "Firmware Klydos", value: "firmware-klydos" },
            ]}
            value={layout}
            onChange={setLayout}
          />
        }
      />
      {layout === "klydos" && <KlydosList />}
      {layout === "labels" && <KlydoLabels />}
      {layout === "firmware-klydos" && <FirmwareKlydos />}
    </>
  );
};

export default Klydos;
